/* eslint-disable react/jsx-max-depth */ /* eslint-disable fp/no-mutation */
import { Info, Popover, Size } from '@transferwise/components';
import { Position } from '@transferwise/components';
import { formatMoney, getRateInAllFormats } from '@transferwise/formatting';
import { Check as CheckIcon } from '@transferwise/icons';
import { More as MoreIcon } from '@transferwise/icons';
import PropTypes from 'prop-types';
import React from 'react';
import { InView } from 'react-intersection-observer';
import { FormattedMessage, injectIntl, useIntl } from 'react-intl';

import CurrencyRouteDescription from '../../common/CurrencyRouteDescription';
import DeliveryEstimationHero from '../../common/DeliveryEstimationHero';
import ExchangeRateIndicator from '../../common/ExchangeRateIndicator';
import FeeDisclaimer from '../../common/FeeDisclaimer';
import Label from '../../common/Label';
import ProviderLogo from '../../common/ProviderLogo';
import ReceiveAmount from '../../common/ReceiveAmount';
import ShowMoreProviders from '../../common/ShowMoreProviders';
import TransferFee from '../../common/TransferFee';
import TransparencyBanner from '../../common/TransparencyBanner';
import { REVOLUT_WITH_DISCLAIMER } from '../../common/providerDisclaimers/ProviderWithDisclaimer';
import ConditionalWrapper from '../../resources/ConditionalWrapper';
import { isWiseAlias } from '../../resources/aliasFilter';
import { isDeliveryEstimationCollectedForAllProviders } from '../../resources/deliveryEstimationService';
import filterQuotes from '../../resources/filterQuotes';
import isConsideredMidmarketRate from '../../resources/isConsideredMidmarketRate';
import isWisePresentAndNotCheapest from '../../resources/isWisePresentAndNotCheapest';
import { isWiseQuote } from '../../resources/quoteFilters';
import { calculateSavingsWithRespectToWiseQuote } from '../../resources/savingsCalculator';
import sortQuotesByReceivedAmount from '../../resources/sortQuotesByReceivedAmount';
import { trackTableViewed, getTrackingTriggerIndex } from '../../resources/tracking';
import { Table } from '../../resources/tracking/types';

import componentStyles from './ComparisonTableHeroContainer.module.less';

function extractHighestReceivedAmount(sortedQuotes) {
  return sortedQuotes.length > 0 ? sortedQuotes[0].receivedAmount : 0;
}

function getProviderDependentStyles(alias, styles) {
  if (isWiseAlias(alias)) {
    return {
      row: styles.featuredRow,
      cell: styles.featuredCell,
      receiveAmountText: styles.receiveAmountHero,
      cheapestDelta: styles.positiveDeltaHighlighted,
      feeText: styles.highlightedFee,
    };
  }
  return {
    row: '',
    cell: '',
    receiveAmountText: styles.receiveAmount,
    cheapestDelta: '',
    feeText: styles.fee,
  };
}

export class ComparisonTableHero extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      showingAllQuotes: false,
      centreProviderViewed: false,
    };
  }

  componentDidMount() {
    this.callOnTableRendered();
  }

  componentDidUpdate() {
    this.callOnTableRendered();
  }

  static getDerivedStateFromProps(props) {
    const { excludePartners, quotes, sourceCurrency, targetCurrency, sendAmount } = props;

    const filteredQuotes = filterQuotes(quotes, { excludePartners });
    const sortedQuotes = sortQuotesByReceivedAmount(filteredQuotes);

    const savingsAgainstWise = calculateSavingsWithRespectToWiseQuote(
      sortedQuotes,
      excludePartners,
    );

    return {
      quotes: sortedQuotes,
      sourceCurrency,
      targetCurrency,
      sendAmount,
      savingsAgainstWise,
    };
  }

  toggleShowAllQuotes = () => {
    this.setState((state) => ({
      showingAllQuotes: !state.showingAllQuotes,
    }));
  };

  setCentreProviderViewed = () => {
    this.setState({
      centreProviderViewed: true,
    });
  };

  callOnTableRendered = () => {
    const { onTableRendered } = this.props;
    onTableRendered(this.state);
  };

  onTableView = (inView) => {
    if (!inView) {
      return;
    }

    this.setCentreProviderViewed();

    const { sendAmount, sourceCurrency, targetCurrency, maxVisibleProviders, trackEvent } =
      this.props;
    const { quotes, showingAllQuotes, savingsAgainstWise } = this.state;
    const visibleQuotes = showingAllQuotes ? quotes : quotes.slice(0, maxVisibleProviders);

    const trackingContext = {
      amount: sendAmount,
      source: sourceCurrency,
      target: targetCurrency,
      quotes: visibleQuotes,
      savingsAgainstWise,
      componentType: Table.Hero,
    };

    trackTableViewed(trackingContext, trackEvent);
  };

  // eslint-disable-next-line sonarjs/cognitive-complexity
  render() {
    const {
      sourceCurrency,
      targetCurrency,
      sendAmount,
      midMarketRate,
      maxVisibleProviders,
      styles,
      deliveryEstimationEnabled,
      isTransparencyFooterHidden,
      intl,
    } = this.props;
    const currentLocale = intl.locale;

    // eslint-disable-next-line react/destructuring-assignment
    const locale = currentLocale.replace('_', '-');

    const derivedStyles = { ...componentStyles, ...styles };
    const { quotes, showingAllQuotes, centreProviderViewed, savingsAgainstWise } = this.state;

    const showDeliveryEstimation =
      deliveryEstimationEnabled && isDeliveryEstimationCollectedForAllProviders(quotes);
    const highestReceivedAmount = extractHighestReceivedAmount(quotes);
    const wisePresentAndNotCheapest = isWisePresentAndNotCheapest(quotes);

    const displayShowMoreProvidersToggle =
      maxVisibleProviders && quotes.length > maxVisibleProviders;
    const visibleQuotes = showingAllQuotes ? quotes : quotes.slice(0, maxVisibleProviders);
    const centreProviderIndex = visibleQuotes
      ? getTrackingTriggerIndex(visibleQuotes.length)
      : null;
    const thClassKey = 'table-hero__th';
    const cellClassKey = 'table-hero__cell';
    const PROVIDERS_WITH_DISCLAIMERS = [REVOLUT_WITH_DISCLAIMER];

    return (
      <>
        <table
          className={`${derivedStyles['table-hero']} ${
            displayShowMoreProvidersToggle ? '' : derivedStyles['without-show-more-option']
          }`}
        >
          <thead className={derivedStyles['table-hero__thead']}>
            <tr className={derivedStyles['table-hero__row']}>
              <th className={derivedStyles[thClassKey]}>
                <FormattedMessage
                  values={{
                    money: formatMoney(sendAmount, sourceCurrency, locale, {
                      alwaysShowDecimals: true,
                    }),
                  }}
                  id="sending-with"
                />
              </th>
              <th className={derivedStyles[thClassKey]}>
                <FormattedMessage id="recipient-gets" />
                <span className={derivedStyles.headingInfo}>
                  (<FormattedMessage id="total-after-fees" />)
                </span>
              </th>
              <th className={derivedStyles[thClassKey]}>
                <FormattedMessage id="transfer-fee" />
              </th>
              <th className={derivedStyles[thClassKey]}>
                <FormattedMessage id="exchange-rate" />
                <span className={derivedStyles.headingInfo}>
                  <CurrencyRouteDescription
                    sourceCurrency={sourceCurrency}
                    targetCurrency={targetCurrency}
                    exchangeRate={midMarketRate}
                    styles={styles}
                  />
                </span>
              </th>
            </tr>
          </thead>
          <tbody>
            {visibleQuotes.map((quote, index) => {
              // TODO: Find a nicer way to parameterise the logo if possible rather than hardcoding.
              const logo = isWiseQuote(quote)
                ? `https://wise.com/public-resources/assets/logos/wise-personal/logo_inverse.svg`
                : quote.logos.normal.svgUrl;
              const isCheapest = quote.receivedAmount === highestReceivedAmount;
              const providerDependentStyles = getProviderDependentStyles(
                quote.alias,
                derivedStyles,
              );
              const ProviderRow = React.forwardRef((props, reference) => {
                const { isCentre } = props;
                return (
                  <tr
                    key={quote.alias}
                    className={`${derivedStyles['table-hero__row']} ${
                      providerDependentStyles.row
                    } ${isWiseQuote(quote) ? 'np-theme-personal--forest-green' : ''}`}
                    ref={reference}
                    data-testid={isCentre ? 'centre-provider-row' : 'provider-row'}
                  >
                    <td
                      className={`${derivedStyles[cellClassKey]} ${providerDependentStyles.cell}`}
                    >
                      <ProviderLogo
                        logo={logo}
                        altText={quote.logos.altText ? quote.logos.altText : quote.name}
                        name={quote.name}
                        alias={quote.alias}
                        isPartner={quote.partner}
                        providersWithDisclaimers={PROVIDERS_WITH_DISCLAIMERS}
                      />
                      {isCheapest && (
                        <span className={derivedStyles.cheapestLabel}>
                          <CheckIcon className={derivedStyles.cheapestLabelTick} />
                          <FormattedMessage id="cheapest" />
                        </span>
                      )}
                    </td>
                    <td className={derivedStyles[cellClassKey]}>
                      <ReceiveAmount
                        styles={{
                          receiveText: `${providerDependentStyles.receiveAmountText} ${derivedStyles.extraPadding}`,
                          negativeDelta: derivedStyles.negativeDeltaHero,
                          positiveDelta: derivedStyles.positiveDeltaHero,
                        }}
                        amount={quote.receivedAmount}
                        currency={targetCurrency}
                        differenceToCheapest={quote.receivedAmount - highestReceivedAmount}
                      />
                      {wisePresentAndNotCheapest && !isWiseQuote(quote) && isCheapest && (
                        <NotCheapestDisclaimer styles={derivedStyles} />
                      )}
                      <MoreInformationPopover
                        styles={derivedStyles}
                        notCheapest={wisePresentAndNotCheapest && !isWiseQuote(quote) && isCheapest}
                        rate={quote.rate}
                        fee={quote.fees}
                        sourceCurrency={sourceCurrency}
                        targetCurrency={targetCurrency}
                        isFeaturedRow={isWiseQuote(quote)}
                      />
                      {isCheapest && isWiseQuote(quote) && savingsAgainstWise > 0 && (
                        <span className={`${providerDependentStyles.cheapestDelta}`}>
                          <FormattedMessage
                            values={{
                              savings: formatMoney(savingsAgainstWise, targetCurrency, locale, {
                                alwaysShowDecimals: true,
                              }),
                            }}
                            id="savings"
                          />
                        </span>
                      )}
                      {quote.feeDisclaimer && (
                        <span>
                          <FeeDisclaimer
                            text={quote.feeDisclaimer}
                            style={derivedStyles['you-receive__fee-disclaimer']}
                          />
                        </span>
                      )}

                      {showDeliveryEstimation && (
                        <DeliveryEstimationHero
                          deliveryEstimation={quote.deliveryEstimation}
                          styles={{
                            'delivery-estimation-hero': `${derivedStyles['delivery-estimation-hero']} ${derivedStyles.extraPadding}`,
                          }}
                        />
                      )}
                    </td>
                    <td className={derivedStyles[cellClassKey]}>
                      <TransferFee
                        styles={{ feeText: `${providerDependentStyles.feeText}` }}
                        currency={sourceCurrency}
                        fee={quote.fees}
                        label={
                          quote.feeDisclaimer ? <FeeDisclaimer text={quote.feeDisclaimer} /> : null
                        }
                      />
                    </td>
                    <td className={derivedStyles[cellClassKey]}>
                      {/* TODO: This ExchangeRateIndicator with midmarket rate label should be extracted out */}
                      <ExchangeRateIndicator
                        styles={{ rateAndIndicator: derivedStyles.exchangeRate }}
                        referenceRate={midMarketRate}
                        rate={quote.rate}
                        label={
                          isConsideredMidmarketRate(quote.rate, midMarketRate) ? (
                            <Label
                              styles={{ labelLink: 'highlightedLabelLink' }}
                              message="mid-market-rate"
                            />
                          ) : null
                        }
                        sourceCurrency={sourceCurrency}
                        targetCurrency={targetCurrency}
                      />
                    </td>
                  </tr>
                );
              });

              const isCentreProvider = index === centreProviderIndex;
              return (
                // eslint-disable-next-line react/jsx-key
                <ConditionalWrapper
                  condition={!centreProviderViewed && isCentreProvider}
                  wrapper={(children) => (
                    <InView
                      onChange={(inView, _) => this.onTableView(inView)}
                      threshold={1}
                      triggerOnce
                    >
                      {({ _, ref }) =>
                        React.cloneElement(children, {
                          ref: ref,
                          isCentre: true,
                        })
                      }
                    </InView>
                  )}
                >
                  <ProviderRow />
                </ConditionalWrapper>
              );
            })}
          </tbody>
        </table>
        {displayShowMoreProvidersToggle && (
          <div className={`text-xs-center p-b-2 ${derivedStyles.toggleContainer}`}>
            <ShowMoreProviders
              styles={{ toggle: derivedStyles.toggleHero }}
              isExpanded={showingAllQuotes}
              onToggle={this.toggleShowAllQuotes}
            />
          </div>
        )}

        {!isTransparencyFooterHidden && <TransparencyBanner styles={derivedStyles} />}
      </>
    );
  }
}

ComparisonTableHero.propTypes = {
  sourceCurrency: PropTypes.string.isRequired,
  targetCurrency: PropTypes.string.isRequired,
  sendAmount: PropTypes.number.isRequired,

  // eslint-disable-next-line react/no-unused-prop-types
  quotes: PropTypes.arrayOf(
    PropTypes.shape({
      // eslint-disable-line react/no-unused-prop-types
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
      alias: PropTypes.string.isRequired,
      logos: PropTypes.shape({
        normal: PropTypes.shape({
          svgUrl: PropTypes.string,
          pngUrl: PropTypes.string,
        }),
        altText: PropTypes.string.isRequired,
      }),
      partner: PropTypes.bool,
      rate: PropTypes.number.isRequired,
      fees: PropTypes.number.isRequired,
      receivedAmount: PropTypes.number.isRequired,
      dateCollected: PropTypes.string.isRequired,
    }),
  ).isRequired,

  midMarketRate: PropTypes.number,

  styles: PropTypes.shape({
    featuredRow: PropTypes.string,
    featuredCell: PropTypes.string,
    receiveAmountHero: PropTypes.string,
    positiveDeltaHighlighted: PropTypes.string,
    toggleHero: PropTypes.string,
    highlightedFee: PropTypes.string,
    receiveAmount: PropTypes.string,
    fee: PropTypes.string,
    'table-hero': PropTypes.string,
    'table-hero__thead': PropTypes.string,
    'table-hero__row': PropTypes.string,
    'table-hero__th': PropTypes.string,
    headingInfo: PropTypes.string,
    'table-hero__cell': PropTypes.string,
    cheapestLabel: PropTypes.string,
    cheapestLabelTick: PropTypes.string,
    negativeDeltaHero: PropTypes.string,
    positiveDeltaHero: PropTypes.string,
    exchangeRate: PropTypes.string,
    disclaimerPopover: PropTypes.string,
    helpCircle: PropTypes.string,
    moreInformationPopover: PropTypes.string,
    alignmentContainer: PropTypes.string,
    alignLeft: PropTypes.string,
    alignRight: PropTypes.string,
    extraPadding: PropTypes.string,
    'delivery-estimation-hero': PropTypes.string,
  }),

  excludePartners: PropTypes.bool, // eslint-disable-line react/no-unused-prop-types
  maxVisibleProviders: PropTypes.number,
  onTableRendered: PropTypes.func,
  deliveryEstimationEnabled: PropTypes.bool,
  isTransparencyFooterHidden: PropTypes.bool,
  trackEvent: PropTypes.func,
};

ComparisonTableHero.defaultProps = {
  styles: {},
  midMarketRate: undefined,
  excludePartners: true,
  maxVisibleProviders: undefined,
  deliveryEstimationEnabled: false,
  onTableRendered: () => {},
  isTransparencyFooterHidden: false,
  trackEvent: () => {},
};

const NotCheapestDisclaimer = ({ styles }) => {
  // eslint-disable-next-line jest/unbound-method
  const { formatMessage } = useIntl();

  return (
    <div className={styles.disclaimerPopover}>
      <Info
        content={<FormattedMessage id="not-cheapest-disclaimer" />}
        size={Size.SMALL}
        aria-label={formatMessage({ id: 'disclaimer' })}
      />
    </div>
  );
};

NotCheapestDisclaimer.propTypes = {
  styles: PropTypes.shape({
    disclaimerPopover: PropTypes.string,
    helpCircle: PropTypes.string,
  }),
};

NotCheapestDisclaimer.defaultProps = {
  styles: {},
};

const MoreInformationPopover = ({
  styles,
  notCheapest,
  rate,
  fee,
  sourceCurrency,
  targetCurrency,
  isFeaturedRow,
}) => {
  const { formatMessage } = useIntl();

  const content = (
    <MoreInformationContent
      styles={styles}
      notCheapest={notCheapest}
      rate={rate}
      fee={fee}
      sourceCurrency={sourceCurrency}
      targetCurrency={targetCurrency}
    />
  );

  return (
    <span className={styles.moreInformationPopover}>
      <Popover content={content} preferredPlacement={Position.BOTTOM}>
        <button
          type="button"
          aria-label={formatMessage({ id: 'more-info' })}
          className="btn-unstyled "
          children={<MoreIcon size={24} className={isFeaturedRow ? styles.featuredRow : ''} />}
        />
      </Popover>
    </span>
  );
};

const MoreInformationContent = ({
  styles,
  notCheapest,
  rate,
  fee,
  sourceCurrency,
  targetCurrency,
}) => {
  const { locale } = useIntl();
  const rateInAllFormats = getRateInAllFormats(rate, sourceCurrency, targetCurrency, {}, locale);

  return (
    <span>
      <span className={styles.alignmentContainer}>
        <p className={styles.alignLeft}>
          <FormattedMessage id="exchange-rate" />
        </p>
        <p className={styles.alignRight}>
          <b>
            {rateInAllFormats ? rateInAllFormats.formats?.equation?.calculationInDecimal : null}
          </b>
        </p>
      </span>
      <span className={styles.alignmentContainer}>
        <p className={styles.alignLeft}>
          <FormattedMessage id="transfer-fee" />
        </p>
        <p className={styles.alignRight}>
          <b>{formatMoney(fee, sourceCurrency, locale, { alwaysShowDecimals: true })}</b>
        </p>
      </span>
      <span>{notCheapest && <FormattedMessage id="not-cheapest-disclaimer" />}</span>
    </span>
  );
};

MoreInformationPopover.propTypes = {
  styles: PropTypes.shape({
    moreInformationPopover: PropTypes.string,
    featuredRow: PropTypes.string,
    alignmentContainer: PropTypes.string,
    alignLeft: PropTypes.string,
    alignRight: PropTypes.string,
  }),
};

MoreInformationPopover.defaultProps = {
  styles: {},
};

export default injectIntl(ComparisonTableHero);
